import React, { useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setCurrentStep,
  setApplyConfirmOpen,
  applyCommissionBatchV14,
  clearBatchResult,
  resetWizard,
  fetchExpectationsPage,
} from "../../../redux/Commission/commissionStatementSlice";
import ApplyConfirmModal from "./ApplyConfirmModal";
import apiServices from "../../../service";

const NAVY = "#205393";
const T1 = "#0f172a";
const T2 = "#475569";
const T3 = "#94a3b8";
const GRN = "#2b8a3e";
const GRNL = "#e6f9f0";
const GRND = "#1b5e20";
const RED = "#c92a2a";
const REDL = "#ffe6e6";
const ORA = "#c05621";
const BRD = "#e2e5ea";
const BRD2 = "#c8cdd5";
const SUR2 = "#f0f0f0";
const SEL_BG = "rgba(32,83,147,0.06)";


const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const FullscreenIcon = ({ active }: { active: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    {active ? (
      <>
        <polyline points="8 3 3 3 3 8" /><line x1="3" y1="3" x2="10" y2="10" />
        <polyline points="16 21 21 21 21 16" /><line x1="21" y1="21" x2="14" y2="14" />
      </>
    ) : (
      <>
        <polyline points="15 3 21 3 21 9" /><line x1="21" y1="3" x2="14" y2="10" />
        <polyline points="9 21 3 21 3 15" /><line x1="3" y1="21" x2="10" y2="14" />
      </>
    )}
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const PAGE_SIZE = 200;

const StepApplyReceipts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const userId = useSelector((s: RootState) => s.authUser?.userId ?? "");
  const {
    expectations,
    selectedDetailIds,
    selectedExpectationIds,
    actualAmounts: actualAmountsMap,
    statementId,
    receiptDate,
    providerId,
    startDate,
    endDate,
    batchResult,
    applyConfirmOpen,
    loading,
    error,
  } = useSelector((s: RootState) => s.commissionStatement);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [fullScreen, setFullScreen] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const selectedExps = useMemo(
    () => expectations.filter((e) => selectedExpectationIds.has(e.CommissionExpectationId)),
    [expectations, selectedExpectationIds]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return selectedExps;
    const q = search.toLowerCase();
    return selectedExps.filter((e) => e.CaseRef.toLowerCase().includes(q));
  }, [selectedExps, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pageRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalSelectedAmount = useMemo(
    () => selectedExps.reduce((sum, e) => {
      const raw = actualAmountsMap[e.CommissionExpectationId] ?? e.ExpectedAmount ?? "0";
      const v = parseFloat(String(raw));
      return sum + (isNaN(v) ? 0 : v);
    }, 0),
    [selectedExps, actualAmountsMap]
  );

  const remainingUnreceiptedCount = useMemo(
    () => expectations.filter((e) => e.CommissionReceiptId === null && !selectedExpectationIds.has(e.CommissionExpectationId)).length,
    [expectations, selectedExpectationIds]
  );

  const handleApply = async () => {
    if (!statementId) return;
    const selectedExpectations = selectedExps.map((e) => ({
      expectationId: e.CommissionExpectationId,
      actualAmount: parseFloat(String(actualAmountsMap[e.CommissionExpectationId] ?? e.ExpectedAmount ?? "0")) || 0,
    }));
    await dispatch(
      applyCommissionBatchV14({
        statementId,
        receiptDate,
        processedStatementRows: selectedDetailIds,
        selectedExpectations,
      })
    );
  };

  const handleDownloadReport = useCallback(async () => {
    if (!statementId) return;
    setDownloadingReport(true);
    try {
      const res = await apiServices.commissionStatement.generateCommissionStatementReport(statementId);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `Commission-Statement-${statementId}-${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_e) {
      // silent
    } finally {
      setDownloadingReport(false);
    }
  }, [statementId]);

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const containerStyle: React.CSSProperties = fullScreen
    ? { position: "fixed", inset: 0, background: "#fff", zIndex: 900, padding: "16px", overflowY: "auto", display: "flex", flexDirection: "column" }
    : {};

  const content = (
    <div style={{ flex: 1 }}>
      {/* Success banner */}
      {batchResult && (
        <div style={{ marginBottom: "14px", background: GRNL, border: "1px solid #a5d6a7", borderRadius: "5px", padding: "14px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#fff", border: "2px solid #a5d6a7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GRN} strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: GRND }}>Receipts Applied Successfully</div>
              {batchResult.partial && (
                <div style={{ fontSize: "11.5px", color: ORA, marginTop: "4px" }}>
                  Warning: server confirmed fewer receipts ({batchResult.appliedCount}) than requested. Some expectations may not have been receipted — please verify before continuing.
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginLeft: "auto" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "18px", fontWeight: 700, fontFamily: "monospace", color: GRN }}>
                  £{batchResult.totalAmount?.toFixed(2)}
                </div>
                <div style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: GRN, marginTop: "2px" }}>Total Applied</div>
              </div>
              <div style={{ width: "1px", background: "rgba(43,138,62,0.2)", alignSelf: "stretch" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "18px", fontWeight: 700, fontFamily: "monospace", color: GRN }}>
                  {formatDate(receiptDate)}
                </div>
                <div style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: GRN, marginTop: "2px" }}>Receipt Date</div>
              </div>
              {remainingUnreceiptedCount > 0 && (
                <>
                  <div style={{ width: "1px", background: "rgba(43,138,62,0.2)", alignSelf: "stretch" }} />
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "18px", fontWeight: 700, fontFamily: "monospace", color: ORA }}>{remainingUnreceiptedCount}</div>
                    <div style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: ORA, marginTop: "2px" }}>Remaining</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {!batchResult && (
        <>
          {/* Collapsible summary panel */}
          <div style={{ border: `1px solid ${BRD}`, borderRadius: "5px", marginBottom: "12px", overflow: "hidden" }}>
            <button
              onClick={() => setSummaryOpen((o) => !o)}
              style={{ width: "100%", background: SUR2, border: "none", padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", textAlign: "left" }}
            >
              <span style={{ fontSize: "12.5px", fontWeight: 600, color: T1, flex: 1 }}>
                Selection Summary
              </span>
              <span style={{ fontSize: "12px", color: T3, marginRight: "6px" }}>
                {selectedExps.length} record{selectedExps.length !== 1 ? "s" : ""} · £{totalSelectedAmount.toFixed(2)}
              </span>
              <ChevronIcon open={summaryOpen} />
            </button>
            {summaryOpen && (
              <div style={{ padding: "10px 14px", display: "flex", gap: "10px", flexWrap: "wrap", borderTop: `1px solid ${BRD}`, background: "#fff" }}>
                {[
                  { label: "Total Expectations", value: expectations.length,                    bg: "rgba(32,83,147,0.05)", color: NAVY },
                  { label: "Selected",            value: selectedExps.length,                   bg: GRNL,  color: GRN },
                  { label: "Selected Amount",     value: `£${totalSelectedAmount.toFixed(2)}`,  bg: "#eef2ff", color: "#3b5bdb" },
                  { label: "Payment Rows",        value: selectedDetailIds.length,              bg: "rgba(32,83,147,0.05)", color: NAVY },
                ].map(({ label, value, bg, color }) => (
                  <div key={label} style={{ flex: 1, background: bg, borderRadius: "5px", padding: "10px 14px", textAlign: "center", minWidth: "100px" }}>
                    <div style={{ fontSize: "20px", fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: "11px", color, marginTop: "2px" }}>{label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Apply error */}
          {error.apply && (
            <div style={{ background: REDL, border: `1px solid ${RED}`, borderRadius: "5px", padding: "8px 12px", fontSize: "12.5px", color: RED, marginBottom: "10px" }}>
              {error.apply}
            </div>
          )}

          {/* Table */}
          <div style={{ border: `1px solid ${BRD}`, borderRadius: "5px", overflow: "hidden", marginBottom: "10px" }}>
            <div style={{ background: SUR2, borderBottom: `1px solid ${BRD}`, padding: "7px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: "7px", top: "50%", transform: "translateY(-50%)", color: T3, pointerEvents: "none" }}>
                    <SearchIcon />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by case ref…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    style={{ height: "28px", padding: "0 8px 0 26px", border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12px", color: T1, background: "#fff", outline: "none", width: "180px" }}
                  />
                </div>
                <span style={{ fontSize: "12px", color: T3, whiteSpace: "nowrap" }}>
                  {selectedExps.length} selected (all pages)
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button
                  onClick={() => setFullScreen((f) => !f)}
                  title={fullScreen ? "Exit full screen" : "Full screen"}
                  style={{ background: "#fff", border: `1px solid ${BRD2}`, borderRadius: "5px", width: "28px", height: "28px", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T2 }}
                >
                  <FullscreenIcon active={fullScreen} />
                </button>
              </div>
            </div>

            <div style={{ overflowX: "auto", maxHeight: fullScreen ? "calc(100vh - 310px)" : "420px", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    {["Case Ref", "Expected Amount", "Actual Amount", "Due Date", "Client (DB)", ""].map((h, i) => (
                      <th key={i} style={{ background: SUR2, borderBottom: `2px solid ${BRD}`, padding: "8px 11px", textAlign: (i === 1 || i === 2) ? "right" : "left", fontSize: "11.5px", fontWeight: 600, color: T2, whiteSpace: "nowrap", width: i === 5 ? "36px" : undefined }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((exp, i) => {
                    const raw = actualAmountsMap[exp.CommissionExpectationId] ?? exp.ExpectedAmount ?? "0";
                    return (
                      <tr key={`${exp.CommissionExpectationId}-${i}`}
                        style={{ borderBottom: `1px solid ${BRD}`, background: SEL_BG }}
                      >
                        <td style={{ padding: "8px 11px", fontFamily: "monospace", fontSize: "11.5px", color: T1 }}>
                          {exp.CaseRef || <span style={{ color: T3 }}>(blank)</span>}
                        </td>
                        <td style={{ padding: "8px 11px", textAlign: "right", fontFamily: "monospace", fontSize: "12px" }}>
                          {exp.ExpectedAmount != null ? `£${Number(exp.ExpectedAmount).toFixed(2)}` : "—"}
                        </td>
                        <td style={{ padding: "8px 11px", textAlign: "right", fontFamily: "monospace", fontSize: "12px", fontWeight: 600, color: NAVY }}>
                          {`£${(parseFloat(String(raw)) || 0).toFixed(2)}`}
                        </td>
                        <td style={{ padding: "8px 11px", color: T1 }}>
                          {exp.DueDate ? new Date(exp.DueDate).toLocaleDateString("en-GB") : "—"}
                        </td>
                        <td style={{ padding: "8px 11px", color: T1 }}>{exp.ClientNameDb || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", borderTop: `1px solid ${BRD}`, fontSize: "12px", color: T2, background: SUR2 }}>
                <span>
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
                </span>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button onClick={() => setPage(page - 1)} disabled={page === 1} style={{ background: "#fff", border: `1px solid ${BRD2}`, borderRadius: "4px", padding: "2px 8px", cursor: page === 1 ? "not-allowed" : "pointer", fontSize: "14px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
                  <button onClick={() => setPage(page + 1)} disabled={page === totalPages} style={{ background: "#fff", border: `1px solid ${BRD2}`, borderRadius: "4px", padding: "2px 8px", cursor: page === totalPages ? "not-allowed" : "pointer", fontSize: "14px", opacity: page === totalPages ? 0.4 : 1 }}>›</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Button row */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${BRD}` }}>
        {!batchResult ? (
          <>
            <button
              onClick={() => dispatch(setCurrentStep(3))}
              style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 14px", height: "32px", cursor: "pointer" }}
            >
              ← Back
            </button>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => dispatch(setApplyConfirmOpen(true))}
              disabled={selectedExps.length === 0 || loading.apply}
              style={{
                background: selectedExps.length > 0 && !loading.apply ? NAVY : "#c8cdd5",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                fontSize: "12.5px",
                fontWeight: 500,
                padding: "0 16px",
                height: "32px",
                cursor: selectedExps.length > 0 && !loading.apply ? "pointer" : "not-allowed",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {loading.apply ? <Spinner size="tiny" /> : "Apply Matched Receipts"}
            </button>
          </>
        ) : (
          <>
            {remainingUnreceiptedCount > 0 && (
              <button
                onClick={() => {
                  if (userId) {
                    dispatch(fetchExpectationsPage({ userId, startDate, endDate, providerId, page: 1 }));
                  }
                  dispatch(clearBatchResult());
                  dispatch(setCurrentStep(3));
                }}
                style={{ background: NAVY, color: "#fff", border: "none", borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 16px", height: "32px", cursor: "pointer" }}
              >
                Continue Receipting →
              </button>
            )}
            <button
              onClick={handleDownloadReport}
              disabled={downloadingReport || !statementId}
              style={{
                background: "#fff", color: NAVY, border: `1px solid ${NAVY}`, borderRadius: "5px",
                fontSize: "12.5px", fontWeight: 500, padding: "0 14px", height: "32px",
                cursor: downloadingReport || !statementId ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: "6px",
                opacity: downloadingReport || !statementId ? 0.6 : 1,
              }}
            >
              {downloadingReport ? <Spinner size="tiny" /> : <DownloadIcon />}
              Download Report
            </button>
            <button
              onClick={() => dispatch(resetWizard())}
              style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 14px", height: "32px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px" }}
            >
              ↻ New Upload
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {fullScreen ? (
        <div style={containerStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "14px", fontWeight: 700, color: T1 }}>Apply Matched Receipts</span>
            <button
              onClick={() => setFullScreen(false)}
              style={{ background: "none", border: `1px solid ${BRD2}`, borderRadius: "5px", padding: "4px 10px", cursor: "pointer", fontSize: "12px", color: T2, display: "inline-flex", alignItems: "center", gap: "5px" }}
            >
              <FullscreenIcon active={true} /> Exit Full Screen
            </button>
          </div>
          {content}
        </div>
      ) : content}

      <ApplyConfirmModal
        open={applyConfirmOpen}
        selectedCount={selectedExps.length}
        totalAmount={totalSelectedAmount}
        remainingCount={remainingUnreceiptedCount}
        applyError={error.apply}
        onConfirm={handleApply}
        onCancel={() => dispatch(setApplyConfirmOpen(false))}
      />
    </>
  );
};

export default StepApplyReceipts;
